import { useState, useMemo } from "react";
import { useAdmin } from "./AdminContext";
import OrderCard from "./OrderCard";
import { ClipboardList, History, Download, Search, X, Filter } from "lucide-react";

function OrderFilters({ orders, search, setSearch, filters, setFilters, statusLabels, statusColors }) {
  const [showFilters, setShowFilters] = useState(false);

  const uniqueProducts = useMemo(() => [...new Set(orders.map(o => o.product_name).filter(Boolean))].sort(), [orders]);
  const uniqueStatuses = useMemo(() => [...new Set(orders.map(o => o.status).filter(Boolean))].sort(), [orders]);
  const uniquePaymentStatuses = useMemo(() => [...new Set(orders.map(o => o.payment_status).filter(Boolean))].sort(), [orders]);
  const uniqueTypes = useMemo(() => [...new Set(orders.map(o => o.customization_type).filter(Boolean))].sort(), [orders]);

  const paymentLabels = { pending: "Pending", authorized: "Authorized", captured: "Captured", cancelled: "Released", paid: "Paid" };
  const activeFilterCount = Object.values(filters).filter(v => v && v !== "all").length + (search ? 1 : 0);

  return (
    <div className="mb-6 space-y-3">
      {/* Search bar */}
      <div className="flex gap-2 items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" strokeWidth={2.5} />
          <input
            type="text"
            className="brutal-input w-full pl-10 pr-10 text-sm"
            placeholder="Search by order #, email, customer name, or product..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="orders-search-input"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-black" data-testid="clear-search-btn">
              <X size={14} strokeWidth={2.5} />
            </button>
          )}
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`brutal-btn text-xs px-4 py-2.5 flex items-center gap-2 ${showFilters || activeFilterCount > 0 ? "bg-[#FF3366] text-white" : "brutal-btn-outline"}`}
          data-testid="toggle-filters-btn"
        >
          <Filter size={14} strokeWidth={2.5} />
          Filters
          {activeFilterCount > 0 && <span className="bg-black text-white text-[10px] font-mono w-5 h-5 flex items-center justify-center">{activeFilterCount}</span>}
        </button>
      </div>

      {/* Filter dropdowns */}
      {showFilters && (
        <div className="brutal-card p-4 flex flex-wrap gap-4 items-end" data-testid="filters-panel">
          <div className="min-w-[140px]">
            <label className="font-heading font-bold text-[10px] uppercase tracking-wider block mb-1">Status</label>
            <select className="brutal-input text-xs w-full" value={filters.status || "all"} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} data-testid="filter-status">
              <option value="all">All Statuses</option>
              {uniqueStatuses.map(s => <option key={s} value={s}>{statusLabels[s] || s}</option>)}
            </select>
          </div>
          <div className="min-w-[140px]">
            <label className="font-heading font-bold text-[10px] uppercase tracking-wider block mb-1">Payment</label>
            <select className="brutal-input text-xs w-full" value={filters.payment || "all"} onChange={e => setFilters(f => ({ ...f, payment: e.target.value }))} data-testid="filter-payment">
              <option value="all">All Payments</option>
              {uniquePaymentStatuses.map(s => <option key={s} value={s}>{paymentLabels[s] || s}</option>)}
            </select>
          </div>
          <div className="min-w-[140px]">
            <label className="font-heading font-bold text-[10px] uppercase tracking-wider block mb-1">Product</label>
            <select className="brutal-input text-xs w-full" value={filters.product || "all"} onChange={e => setFilters(f => ({ ...f, product: e.target.value }))} data-testid="filter-product">
              <option value="all">All Products</option>
              {uniqueProducts.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="min-w-[140px]">
            <label className="font-heading font-bold text-[10px] uppercase tracking-wider block mb-1">Type</label>
            <select className="brutal-input text-xs w-full" value={filters.type || "all"} onChange={e => setFilters(f => ({ ...f, type: e.target.value }))} data-testid="filter-type">
              <option value="all">All Types</option>
              {uniqueTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="min-w-[120px]">
            <label className="font-heading font-bold text-[10px] uppercase tracking-wider block mb-1">Pickup</label>
            <select className="brutal-input text-xs w-full" value={filters.pickup || "all"} onChange={e => setFilters(f => ({ ...f, pickup: e.target.value }))} data-testid="filter-pickup">
              <option value="all">All</option>
              <option value="pickup">Pickup Only</option>
              <option value="shipping">Shipping Only</option>
            </select>
          </div>
          <button
            onClick={() => setFilters({})}
            className="brutal-btn brutal-btn-outline text-xs px-3 py-2.5 flex items-center gap-1 border-red-400 text-red-500 hover:bg-red-50"
            data-testid="clear-filters-btn"
          >
            <X size={12} strokeWidth={2.5} /> Clear All
          </button>
        </div>
      )}
    </div>
  );
}

function useFilteredOrders(orders, search, filters) {
  return useMemo(() => {
    let result = orders;

    if (search.trim()) {
      const q = search.toLowerCase().trim();
      result = result.filter(o =>
        o.id?.toLowerCase().includes(q) ||
        o.shipping_email?.toLowerCase().includes(q) ||
        o.shipping_name?.toLowerCase().includes(q) ||
        o.product_name?.toLowerCase().includes(q) ||
        o.customization_text?.toLowerCase().includes(q)
      );
    }

    if (filters.status && filters.status !== "all") {
      result = result.filter(o => o.status === filters.status);
    }
    if (filters.payment && filters.payment !== "all") {
      result = result.filter(o => o.payment_status === filters.payment);
    }
    if (filters.product && filters.product !== "all") {
      result = result.filter(o => o.product_name === filters.product);
    }
    if (filters.type && filters.type !== "all") {
      result = result.filter(o => o.customization_type === filters.type);
    }
    if (filters.pickup === "pickup") {
      result = result.filter(o => o.pickup_on_site);
    } else if (filters.pickup === "shipping") {
      result = result.filter(o => !o.pickup_on_site);
    }

    return result;
  }, [orders, search, filters]);
}

export function AdminOrders() {
  const { activeOrders, ordersLoading, expandedOrder, toggleExpandOrder, updateOrderStatus, statusColors, statusLabels, formatDateTime, setPickedUpDialogOpen, setPickedUpOrderId, setRejectDialogOpen, setRejectOrderId, setRejectReason, setRejectCustomReason, setCaptureDialogOpen, setCaptureOrderId, setCaptureMode, setCaptureAmount, setCaptureAuthorizedAmount } = useAdmin();

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});
  const filtered = useFilteredOrders(activeOrders, search, filters);

  const openReject = (orderId) => { setRejectOrderId(orderId); setRejectReason(""); setRejectCustomReason(""); setRejectDialogOpen(true); };
  const openCapture = (order) => { setCaptureOrderId(order.id); setCaptureMode("full"); setCaptureAmount(""); setCaptureAuthorizedAmount(order.authorized_amount || order.total || 0); setCaptureDialogOpen(true); };
  const openPickedUp = (orderId) => { setPickedUpOrderId(orderId); setPickedUpDialogOpen(true); };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading text-2xl font-black uppercase">Active Orders</h2>
          <p className="text-xs text-zinc-400 font-mono mt-1">
            {filtered.length === activeOrders.length
              ? `${activeOrders.length} active order(s)`
              : `${filtered.length} of ${activeOrders.length} order(s)`}
          </p>
        </div>
      </div>

      <OrderFilters orders={activeOrders} search={search} setSearch={setSearch} filters={filters} setFilters={setFilters} statusLabels={statusLabels} statusColors={statusColors} />

      {ordersLoading ? <p className="text-zinc-400 text-sm">Loading orders...</p> : filtered.length === 0 ? (
        <div className="brutal-card p-12 text-center">
          <ClipboardList size={48} className="mx-auto mb-4 text-zinc-300" strokeWidth={1.5} />
          <p className="text-zinc-400 font-heading font-bold uppercase">{search || Object.values(filters).some(v => v && v !== "all") ? "No orders match your search" : "No Active Orders"}</p>
          {(search || Object.values(filters).some(v => v && v !== "all")) && (
            <button onClick={() => { setSearch(""); setFilters({}); }} className="brutal-btn brutal-btn-outline text-xs mt-4 px-4 py-2" data-testid="clear-all-btn">Clear Search & Filters</button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map(order => (
            <OrderCard key={order.id} order={order} expanded={expandedOrder === order.id} onToggle={() => toggleExpandOrder(order.id)} onStatusChange={updateOrderStatus} statusColors={statusColors} statusLabels={statusLabels} formatDateTime={formatDateTime}
              onPickedUp={() => openPickedUp(order.id)} onReject={() => openReject(order.id)} onCapture={() => openCapture(order)} />
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminHistory() {
  const { shippedOrders, expandedOrder, toggleExpandOrder, updateOrderStatus, handleDeleteOrder, handleExportOrders, exporting, statusColors, statusLabels, formatDateTime } = useAdmin();

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});
  const filtered = useFilteredOrders(shippedOrders, search, filters);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading text-2xl font-black uppercase">Order History</h2>
          <p className="text-xs text-zinc-400 font-mono mt-1">
            {filtered.length === shippedOrders.length
              ? `${shippedOrders.length} order(s) shipped/rejected`
              : `${filtered.length} of ${shippedOrders.length} order(s)`}
          </p>
        </div>
        {shippedOrders.length > 0 && (
          <button onClick={handleExportOrders} disabled={exporting} className="brutal-btn text-xs px-4 py-2 flex items-center gap-2" data-testid="export-orders-btn">
            <Download size={14} strokeWidth={2.5} /> {exporting ? "Exporting..." : "Export CSV"}
          </button>
        )}
      </div>

      <OrderFilters orders={shippedOrders} search={search} setSearch={setSearch} filters={filters} setFilters={setFilters} statusLabels={statusLabels} statusColors={statusColors} />

      {filtered.length === 0 ? (
        <div className="brutal-card p-12 text-center">
          <History size={48} className="mx-auto mb-4 text-zinc-300" strokeWidth={1.5} />
          <p className="text-zinc-400 font-heading font-bold uppercase">{search || Object.values(filters).some(v => v && v !== "all") ? "No orders match your search" : "No Order History Yet"}</p>
          {(search || Object.values(filters).some(v => v && v !== "all")) && (
            <button onClick={() => { setSearch(""); setFilters({}); }} className="brutal-btn brutal-btn-outline text-xs mt-4 px-4 py-2">Clear Search & Filters</button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map(order => (
            <OrderCard key={order.id} order={order} expanded={expandedOrder === order.id} onToggle={() => toggleExpandOrder(order.id)} onStatusChange={updateOrderStatus} statusColors={statusColors} statusLabels={statusLabels} formatDateTime={formatDateTime} showDelete onDelete={() => handleDeleteOrder(order.id)} />
          ))}
        </div>
      )}
    </div>
  );
}
